import clearSearchBox from '../actions/clearSearchBox';
import exitSearch from '../actions/exitSearch';
import onGuidedFormulationSuggestionSelected from '../actions/onGuidedFormulationSuggestionSelected';
import onPillSuggestionSelected from '../actions/onPillSuggestionSelected';
import onRemoveGuidedFormulationPill from '../actions/onRemoveGuidedFormulationPill';
import onRemoveSuggestionPill from '../actions/onRemoveSuggestionPill';
import onSearchTextChanged from '../actions/onSearchTextChanged';
import onSelectSuggestionViaKeyboard from '../actions/onSelectSuggestionViaKeyboard';
import onSuggestionSelected from '../actions/onSuggestionSelected';
import startSearch from '../actions/startSearch';
import updateSearchBoxText from '../actions/updateSearchBoxText';
import updateSelectedPillIndex from '../actions/updateSelectedPillIndex';
import updateSelectedSuggestionIndex from '../actions/updateSelectedSuggestionIndex';
import hasSuggestionPills from '../selectors/hasSuggestionPills';
import isSearchBoxEmpty from '../selectors/isSearchBoxEmpty';
import getScenarioStore from '../store/getScenarioStore';
import { createDefaultSearchStore } from '../store/store';
import getSubstrateSearchScenarioBySearchScenarioId from '../utils/getSubstrateSearchScenarioBySearchScenarioId';
import { orchestrator } from 'satcheljs';
import {
    onBackspacePressedSearchInput,
    onDownArrowPressedSearchInput,
    onEnterPressedSearchInput,
    onEscapePressedSearchInput,
    onLeftArrowPressedSearchInput,
    onRightArrowPressedSearchInput,
    onUpArrowPressedSearchInput,
} from '../actions/onKeyDownSearchInput';
import { lazyLogSearchBoxStateChanged, SuggestionKind, SuggestionSet } from 'owa-search-service';

orchestrator(onEnterPressedSearchInput, actionMessage => {
    const { scenarioId, selectedSuggestionIndex } = actionMessage;
    const store = getScenarioStore(scenarioId);

    if (selectedSuggestionIndex !== createDefaultSearchStore().selectedSuggestionIndex) {
        // Dispatch action to let consumer know a suggestion was selected.
        onSuggestionSelected(
            scenarioId,
            store.currentSuggestions.Suggestions[selectedSuggestionIndex],
            'keyboard',
            store.currentSuggestions.TraceId,
            selectedSuggestionIndex
        );

        /**
         * This is specific to the case where the user is arrowing up/down the
         * SuggestionsContextMenu and hits "Enter" on a pill suggestion. We want
         * to clear searchText, searchTextForSuggestion, and reset suggestions.
         */
        if (!store.searchText) {
            onSearchTextChanged('', scenarioId, false /* showSuggestionsUi */);
        }
    } else if (!isSearchBoxEmpty(scenarioId)) {
        startSearch('Keyboard', scenarioId, true /* explicitSearch */);
    }
});

orchestrator(onSelectSuggestionViaKeyboard, actionMessage => {
    const suggestionToSelect = actionMessage.suggestionToSelect;
    const scenarioId = actionMessage.scenarioId;
    const store = getScenarioStore(scenarioId);

    // Dispatch action to clear search box.
    clearSearchBox(scenarioId);

    // Add suggestion to SearchInput (based on kind).
    switch (suggestionToSelect.kind) {
        case SuggestionKind.Category:
        case SuggestionKind.People:
            onPillSuggestionSelected(
                suggestionToSelect,
                false /* suggestionSelected */,
                scenarioId
            );
            break;
        case SuggestionKind.Keywords:
            if (suggestionToSelect.Segments) {
                onGuidedFormulationSuggestionSelected(
                    suggestionToSelect.Segments,
                    false /* suggestionSelected */,
                    scenarioId
                );
            } else {
                updateSearchBoxText(scenarioId, suggestionToSelect.QueryText);
            }
            break;
        case SuggestionKind.Message:
            updateSearchBoxText(scenarioId, store.searchTextForSuggestion);
            break;
        case SuggestionKind.Files:
            updateSearchBoxText(scenarioId, store.searchTextForSuggestion);
            break;
    }

    /**
     * Log that search box state changed because of keyboard navigation
     * (if using 3S and is a 3S suggestion, i.e. QF request ID exists and there
     * is non-empty searchTextForSuggestion).
     */
    if (store.isUsing3S && store.latestTraceId && store.searchTextForSuggestion) {
        lazyLogSearchBoxStateChanged.importAndExecute(
            new Date() /* currentTime */,
            'navigation' /* reason */,
            suggestionToSelect.ReferenceId,
            store.latestTraceId,
            getSubstrateSearchScenarioBySearchScenarioId(scenarioId)
        );
    }
});

orchestrator(onUpArrowPressedSearchInput, actionMessage => {
    const scenarioId = actionMessage.scenarioId;
    const { currentSuggestions, selectedSuggestionIndex } = getScenarioStore(scenarioId);

    if (!shouldNavigateResults(currentSuggestions)) {
        return;
    }

    /**
     * If user has no selected index yet or if they have the first item selected,
     * then wrap the selection by going to the last suggestion in the UI.
     *
     * Else, the user wants to select the previous suggestion in the list so decrease
     * the selection index by 1.
     */
    if (selectedSuggestionIndex === 0 || selectedSuggestionIndex === -1) {
        const indexToSelect = currentSuggestions.Suggestions.length - 1;
        const suggestionToSelect = currentSuggestions.Suggestions[indexToSelect];
        onSelectSuggestionViaKeyboard(indexToSelect, suggestionToSelect, scenarioId);
        updateSelectedSuggestionIndex(scenarioId, indexToSelect);
    } else if (
        selectedSuggestionIndex > 0 &&
        selectedSuggestionIndex < currentSuggestions.Suggestions.length
    ) {
        const indexToSelect = selectedSuggestionIndex - 1;
        const suggestionToSelect = currentSuggestions.Suggestions[indexToSelect];
        onSelectSuggestionViaKeyboard(indexToSelect, suggestionToSelect, scenarioId);
        updateSelectedSuggestionIndex(scenarioId, indexToSelect);
    }
});

orchestrator(onDownArrowPressedSearchInput, actionMessage => {
    const scenarioId = actionMessage.scenarioId;
    const { currentSuggestions, selectedSuggestionIndex } = getScenarioStore(scenarioId);

    if (!shouldNavigateResults(currentSuggestions)) {
        return;
    }

    /**
     * If user has the last suggestion selected, then wrap the selection by going
     * to the first item in the list.
     *
     * Else, the user wants to select the next suggestion in the list so increase
     * the selection index by 1.
     */
    if (selectedSuggestionIndex === currentSuggestions.Suggestions.length - 1) {
        const indexToSelect = 0;
        const suggestionToSelect = currentSuggestions.Suggestions[indexToSelect];
        onSelectSuggestionViaKeyboard(indexToSelect, suggestionToSelect, scenarioId);
        updateSelectedSuggestionIndex(scenarioId, indexToSelect);
    } else if (selectedSuggestionIndex < currentSuggestions.Suggestions.length - 1) {
        const indexToSelect = selectedSuggestionIndex + 1;
        const suggestionToSelect = currentSuggestions.Suggestions[indexToSelect];
        onSelectSuggestionViaKeyboard(indexToSelect, suggestionToSelect, scenarioId);
        updateSelectedSuggestionIndex(scenarioId, indexToSelect);
    }
});

orchestrator(onLeftArrowPressedSearchInput, actionMessage => {
    const scenarioId = actionMessage.scenarioId;
    const store = getScenarioStore(scenarioId);

    const cursorPosition = actionMessage.cursorPosition;
    const selectedPillIndex = store.selectedPillIndex;

    /**
     * If there aren't any suggestion pills or the user's cursor in the search
     * input is not 0, then bail.
     */
    if (!hasSuggestionPills(scenarioId) || cursorPosition !== 0) {
        return;
    }

    /**
     * Case 1: There is not a pill selected.
     * Result: Select the last pill.
     */
    if (selectedPillIndex === createDefaultSearchStore().selectedPillIndex) {
        if (store.suggestionPills.size > 0) {
            updateSelectedPillIndex(scenarioId, store.suggestionPills.size - 1);
        } else if (store.suggestionSegments.size > 0) {
            updateSelectedPillIndex(scenarioId, store.suggestionSegments.size - 1);
        }

        return;
    }

    /**
     * Case 2: There is a pill selected and it's not the first pill.
     * Result: Select the pill to the left (index - 1).
     */
    if (selectedPillIndex !== 0) {
        updateSelectedPillIndex(scenarioId, selectedPillIndex - 1);
        return;
    }

    /**
     * Case 3: There is a pill selected and it's the first pill.
     * Result: It's a no-op (return).
     */
    if (selectedPillIndex === 0) {
        return;
    }
});

orchestrator(onRightArrowPressedSearchInput, actionMessage => {
    const scenarioId = actionMessage.scenarioId;
    const { selectedPillIndex } = getScenarioStore(scenarioId);

    // If there aren't any suggestion pills then bail.
    if (!hasSuggestionPills(scenarioId)) {
        return;
    }

    // Select the pill to the right (index + 1).
    updateSelectedPillIndex(scenarioId, selectedPillIndex + 1);
});

orchestrator(onEscapePressedSearchInput, actionMessage => {
    const { scenarioId, isSuggestionsDropdownVisible } = actionMessage;

    if (!isSuggestionsDropdownVisible) {
        exitSearch('Escape', scenarioId);
    }
});

orchestrator(onBackspacePressedSearchInput, async actionMessage => {
    const scenarioId = actionMessage.scenarioId;
    const store = getScenarioStore(scenarioId);

    /**
     * If the cursor is at the beginning of the search input, then delete the
     * last pill.
     */
    if (actionMessage.cursorPosition === 0) {
        const suggestionPillsCount = store.suggestionPillIds.length;
        const suggestionSegmentsCount = store.suggestionSegments.size;

        if (suggestionPillsCount > 0) {
            // Dispatch action to remove last suggestion pill from store.
            onRemoveSuggestionPill(
                store.suggestionPillIds[suggestionPillsCount - 1],
                scenarioId,
                'BackspaceInput'
            );
        }

        /**
         * Because we can't have segments AND client-side pills right now,
         * this can be a separate block without any repercussions.
         */
        if (suggestionSegmentsCount > 0) {
            // Dispatch action to remove last segment from store.
            const suggestionSegmentKeys = [...store.suggestionSegments.keys()];
            const lastSegmentKey = suggestionSegmentKeys[suggestionSegmentKeys.length - 1];
            onRemoveGuidedFormulationPill(lastSegmentKey, scenarioId);
        }
    }
});

const shouldNavigateResults = (suggestionSet: SuggestionSet): boolean => {
    // Return if there isn't a complete suggestion set in the store.
    if (!suggestionSet || !suggestionSet.IsComplete) {
        return false;
    }

    // Return if there aren't any suggestions.
    const suggestionsCount = suggestionSet.Suggestions.length;
    if (suggestionsCount === 0) {
        return false;
    }

    return true;
};
